import * as React from 'react';
import { TournamentTieBreaker } from 'Src/models/tournament';

export const RANKING_CRITERIA: Array<{ id: TournamentTieBreaker; label: string }> = [
  { id: 'HEAD_TO_HEAD_POINTS', label: 'Head-to-head result' },
  { id: 'HEAD_TO_HEAD_GOAL_DIFFERENCE', label: 'Head-to-head goal difference' },
  { id: 'HEAD_TO_HEAD_GOALS_FOR', label: 'Head-to-head goals scored' },
  { id: 'GOAL_DIFFERENCE', label: 'Goal difference' },
  { id: 'GOALS_FOR', label: 'Goals scored' },
  { id: 'GOALS_AGAINST', label: 'Fewest goals conceded' },
  { id: 'WINS', label: 'Matches won' },
  { id: 'FAIR_PLAY', label: 'Fair play / disciplinary record' },
  { id: 'MANUAL_DRAW', label: 'Manual decision / drawing lots' }
];
interface Props {
  points: number[];
  criteria: Array<{ criterion: TournamentTieBreaker }>;
  onChange: (points: number[], criteria: Array<{ criterion: TournamentTieBreaker }>) => void;
}
export const TournamentRankingEditor: React.FC<Props> = ({ points, criteria, onChange }) => {
  const change = (list: Props['criteria']) =>
    onChange(points, [
      ...list.filter(item => item.criterion !== 'MANUAL_DRAW'),
      ...list.filter(item => item.criterion === 'MANUAL_DRAW')
    ]);
  const move = (index: number, offset: number) => {
    const list = [...criteria];
    [list[index], list[index + offset]] = [list[index + offset], list[index]];
    change(list);
  };
  return (
    <div className="tswRankingEditor">
      <div className="tswRankingPoints">
        {['Win', 'Draw with goals', 'Draw 0–0', 'Loss'].map((label, index) => (
          <label key={label}>
            <span>{label}</span>
            <input
              type="number"
              aria-label={`${label} points`}
              value={points[index]}
              min="-10000"
              max="10000"
              onChange={event =>
                onChange(
                  points.map((value, i) => (i === index ? Number(event.target.value) : value)),
                  criteria
                )
              }
            />
          </label>
        ))}
      </div>
      <p>After points, apply these criteria in order. Manual decision stays last.</p>
      <ol>
        {criteria.map((item, index) => (
          <li key={item.criterion}>
            <select
              aria-label={`Ranking criterion ${index + 1}`}
              value={item.criterion}
              onChange={event =>
                change(
                  criteria.map((entry, i) =>
                    i === index ? { criterion: event.target.value as TournamentTieBreaker } : entry
                  )
                )
              }
            >
              {RANKING_CRITERIA.map(option => (
                <option
                  key={option.id}
                  value={option.id}
                  disabled={option.id !== item.criterion && criteria.some(entry => entry.criterion === option.id)}
                >
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              aria-label="Move criterion up"
              disabled={!index || item.criterion === 'MANUAL_DRAW'}
              onClick={() => move(index, -1)}
            >
              ↑
            </button>
            <button
              type="button"
              aria-label="Move criterion down"
              disabled={
                index === criteria.length - 1 ||
                item.criterion === 'MANUAL_DRAW' ||
                criteria[index + 1]?.criterion === 'MANUAL_DRAW'
              }
              onClick={() => move(index, 1)}
            >
              ↓
            </button>
            <button
              type="button"
              aria-label="Remove ranking criterion"
              disabled={criteria.length === 1}
              onClick={() => change(criteria.filter((_, i) => i !== index))}
            >
              ×
            </button>
          </li>
        ))}
      </ol>
      <button
        type="button"
        disabled={criteria.length === RANKING_CRITERIA.length}
        onClick={() => {
          const next = RANKING_CRITERIA.find(item => !criteria.some(entry => entry.criterion === item.id));
          if (next) change([...criteria, { criterion: next.id }]);
        }}
      >
        + Add criterion
      </button>
    </div>
  );
};
